
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Shield, Flag, MessageCircle, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const QueueSnapshot = () => {
  const { t } = useLanguage();

  const { data: queues, isLoading } = useQuery({
    queryKey: ['queue-snapshot'],
    queryFn: async () => {
      // KYC pending count
      const { count: kycPending } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('kyc_status', 'PENDING');

      // Reports pending count
      const { count: reportsPending } = await supabase
        .from('reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open');

      // Flagged messages count
      const { count: flaggedMessages } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('flagged', true);

      return {
        kyc: kycPending || 0,
        reports: reportsPending || 0,
        flagged: flaggedMessages || 0
      };
    }
  });

  const queueItems = [
    {
      title: t('kyc_to_review', 'التحقق للمراجعة', 'KYC to Review'),
      count: queues?.kyc,
      icon: Shield,
      href: '/admin/kyc',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: t('reports_to_triage', 'التقارير للفرز', 'Reports to Triage'),
      count: queues?.reports,
      icon: Flag,
      href: '/admin/reports',
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    {
      title: t('flagged_chats', 'المحادثات المبلغ عنها', 'Flagged Chats'),
      count: queues?.flagged,
      icon: MessageCircle,
      href: '/admin/reports?type=messages',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    }
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('queues_snapshot', 'لقطة الطوابير', 'Queues Snapshot')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('queues_snapshot', 'لقطة الطوابير', 'Queues Snapshot')}</CardTitle>
        <CardDescription>
          {t('items_requiring_attention', 'العناصر التي تتطلب الانتباه', 'Items requiring attention')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {queueItems.map((item, index) => (
            <Link key={index} to={item.href}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${item.bgColor}`}>
                        <item.icon className={`h-4 w-4 ${item.color}`} />
                      </div>
                      <div>
                        <p className="font-medium">{item.title}</p>
                        <Badge variant={item.count && item.count > 0 ? "destructive" : "secondary"}>
                          {item.count || 0}
                        </Badge>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default QueueSnapshot;
